import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import axiosInstance from "../../api/axiosInstance";
import { BASE_DEV_URL, BASE_URL } from "../../env";

const initialState = {
  sportsInfo: [],
  seriesBySport: {},
  slides: [],
  socialMediaContent: null,
  whatsappUrl: "",
  loading: false,
  error: null,
  unauthorized: false,
};

export const getSportsInfoAsync = createAsyncThunk(
  "sidebar/getSportsInfo",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/sport/getSports");
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch sports info",
      );
    }
  },
);

export const getSlides = createAsyncThunk(
  "sidebar/getSlides",
  async (_, { rejectWithValue }) => {
    try {
      const configuredBaseUrl =
        process.env.NODE_ENV === "development" ? BASE_DEV_URL : BASE_URL;

      // Use parent domain for sliders only (e.g. fairplay.jeetindia.online -> jeetindia.online).
      const parsedUrl = new URL(configuredBaseUrl);
      const hostnameParts = parsedUrl.hostname.split(".");
      const hostWithoutSubdomain =
        hostnameParts.length > 2
          ? hostnameParts.slice(1).join(".")
          : parsedUrl.hostname;

      const apiBasePath = parsedUrl.pathname?.includes("/api/")
        ? parsedUrl.pathname.replace(/\/$/, "")
        : "/api/v1";

      const sliderApiUrl = `${parsedUrl.protocol}//${hostWithoutSubdomain}${apiBasePath}/content/sliders`;
      const response = await axios.get(sliderApiUrl);
      return response.data || response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch slides",
      );
    }
  },
);


export const getSeriesInfoBySportsName = createAsyncThunk(
  "sidebar/getSeriesInfoBySportsName",
  // payload: { sportId, isRacingMarket?: boolean }
  async ({ sportId, isRacingMarket = false }, { rejectWithValue }) => {
    try {
      const endpoint = isRacingMarket
        ? "/market/allRacingMarkets"
        : "/series/series";

      const response = await axiosInstance.post(endpoint, {
        sport_id: String(sportId),
      });

      return { sportId, isRacingMarket, data: response };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch series info",
      );
    }
  },
);

const getRootDomain = () => {
  try {
    const configuredBaseUrl =
      process.env.NODE_ENV === "development" ? BASE_DEV_URL : BASE_URL;
    const parsedUrl = new URL(configuredBaseUrl);
    const parts = parsedUrl.hostname.split(".");
    if (parts.length > 2) return parts.slice(-2).join(".");
    if (parsedUrl.hostname === "localhost") return "jeetindia.online";
    return parsedUrl.hostname;
  } catch {
    return "jeetindia.online";
  }
};

export const fetchSocialMediaContent = createAsyncThunk(
  "sidebar/fetchSocialMediaContent",
  async (loggedIn = false, { rejectWithValue }) => {
    try {
      const endpoint = loggedIn
        ? "/content/getSocialMediaContent"
        : "/content/get";
      const rootDomain = getRootDomain();
      const response = await axiosInstance.post(endpoint, {
        slug: `${rootDomain}-social`,
      });

      const data = response?.data ?? null;
      const descStr = data?.description;
      let parsed = null;

      if (typeof descStr === "string") {
        try {
          parsed = JSON.parse(descStr);
        } catch {
          parsed = null;
        }
      }

      const whatsappUrl =
        parsed?.Whatsapp?.url || parsed?.WhatsApp?.url || parsed?.whatsapp?.url || "";

      return {
        socialMediaContent: parsed,
        whatsappUrl,
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch social media content",
      );
    }
  },
);

const sidebarSlice = createSlice({
  name: "sidebar",
  initialState,
  reducers: {
    setSportsInfo: (state, action) => {
      state.sportsInfo = action.payload;
    },
    clearSocialMediaContent: (state) => {
      state.socialMediaContent = null;
      state.whatsappUrl = "";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSportsInfoAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSportsInfoAsync.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload;
        // If backend says unauthorized, mark flag and keep sportsInfo empty
        if (payload?.status === false && payload?.logout) {
          state.unauthorized = true;
          state.sportsInfo = [];
        } else {
          state.unauthorized = false;
          state.sportsInfo = Array.isArray(payload?.data)
            ? payload.data
            : Array.isArray(payload)
              ? payload
              : [];
        }
        state.error = null;
      })
      .addCase(getSportsInfoAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getSlides.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSlides.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload;
        state.slides = Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload)
            ? payload
            : [];
        state.error = null;
      })
      .addCase(getSlides.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getSeriesInfoBySportsName.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSeriesInfoBySportsName.fulfilled, (state, action) => {
        state.loading = false;
        const { sportId, isRacingMarket, data } = action.payload || {};
        const key = sportId != null ? String(sportId) : undefined;
        if (!key) return;
        let raw =
          data?.data !== undefined
            ? data.data
            : Array.isArray(data)
              ? data
              : [];
        // Normalize: API may return object keyed by index for racing markets
        if (!Array.isArray(raw) && raw && typeof raw === "object") {
          raw = Object.values(raw);
        }
        let seriesList = Array.isArray(raw) ? raw : [];

        // When there are no markets/series, backend may return only msg/status
        if (!seriesList.length && !isRacingMarket) {
          const label = data?.msg || "No markets";
          seriesList = [{ series_name: label }];
        }

        state.seriesBySport[key] = seriesList;
        state.error = null;
      })
      .addCase(getSeriesInfoBySportsName.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchSocialMediaContent.fulfilled, (state, action) => {
        state.socialMediaContent = action.payload?.socialMediaContent || null;
        state.whatsappUrl = action.payload?.whatsappUrl || "";
      })
      .addCase(fetchSocialMediaContent.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const { setSportsInfo, clearSocialMediaContent } = sidebarSlice.actions;
export default sidebarSlice.reducer;